import { z } from 'zod'

import { AUTHORIZATION_READER_DEV_PROXY_PATH } from '../../lib/authorization-reader-endpoint'
import { getFirebaseServices } from '../../services/firebase'
import type { TenantRepository } from './tenant-repository'

const sessionSchema = z.object({
  authority: z.enum(['RTDB', 'POSTGRESQL']),
  cohortDigest: z.string().regex(/^[0-9a-f]{12}$/),
  revision: z.number().int().positive(),
  callerClinicAvailable: z.boolean(),
  branches: z.array(
    z.object({
      id: z.string().min(1).max(128),
      name: z.string().min(1).max(160),
    })
  ),
})

type Session = z.infer<typeof sessionSchema>
const inFlight = new Map<string, Promise<Session>>()
const retryDelaysMs = [500, 1000]
const transientStatuses = new Set([429, 500, 502, 503, 504])

async function fetchSession(
  projectId: string,
  callerUid: string,
  token: string
) {
  for (let attempt = 0; ; attempt += 1) {
    if (getFirebaseServices().auth.currentUser?.uid !== callerUid) {
      throw new Error('Authenticated caller is unavailable')
    }
    let response: Response | undefined
    try {
      response = await fetch(
        import.meta.env.DEV
          ? `${AUTHORIZATION_READER_DEV_PROXY_PATH}/session`
          : `https://us-central1-${projectId}.cloudfunctions.net/authorization-reader/session`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      )
    } catch (error) {
      // A platform-aborted CORS preflight is exposed by fetch as TypeError.
      if (!(error instanceof TypeError) || attempt >= retryDelaysMs.length) {
        throw error
      }
    }
    if (response) {
      if (response.ok) return sessionSchema.parse(await response.json())
      if (
        !transientStatuses.has(response.status) ||
        attempt >= retryDelaysMs.length
      ) {
        throw new Error('Authorization reader is unavailable')
      }
    }
    // Retry only the same fenced read; never substitute cached or RTDB grants.
    await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]))
  }
}

function loadSession(callerUid: string): Promise<Session> {
  const current = inFlight.get(callerUid)
  if (current) return current
  const services = getFirebaseServices()
  const projectId = services.auth.app.options.projectId
  const user = services.auth.currentUser
  if (!projectId || !user || user.uid !== callerUid) {
    return Promise.reject(new Error('Authenticated caller is unavailable'))
  }
  const pending = user
    .getIdToken()
    .then((token) => fetchSession(projectId, callerUid, token))
    .finally(() => inFlight.delete(callerUid))
  inFlight.set(callerUid, pending)
  return pending
}

export const authorizationTenantRepository: TenantRepository = {
  async hasCallerClinic(callerUid) {
    return (await loadSession(callerUid)).callerClinicAvailable
  },
  async getBranches(callerUid) {
    return (await loadSession(callerUid)).branches
  },
}
